'use client';

import { logger } from '@adornis/base/logging.js';
import { logout } from '@adornis/users/client/loginFunctions.js';
import {
  ArrowLeft,
  BookOpen,
  Brain,
  Check,
  ChevronDown,
  ClipboardCheck,
  FileText,
  Key,
  ListChecks,
  LogOut,
  Mail,
  MessageSquare,
  Pencil,
  Plus,
  School,
  Shield,
  SpellCheck,
  User,
  X,
} from 'lucide-react';
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { editStudentSubjectsMutation } from '../../../db/api/edit-student.js';
import { editTeacherMemoryMutation } from '../../../db/api/edit-teacher-memory.js';
import { editTeacherMutation } from '../../../db/api/edit-teacher.js';
import { getStudentByUserIdQuery } from '../../../db/api/get-student.js';
import { getTeacherByUserIdQuery } from '../../../db/api/get-teacher.js';
import { updateGrammarPreferencesMutation } from '../../../db/api/update-grammar-preferences.js';
import { Student } from '../../../db/student.js';
import { Badge } from '../ui/badge.js';
import { ALL_SUBJECTS, isSubject, type Subject } from '../../../db/subjects.js';
import { Teacher } from '../../../db/teacher.js';
import { contentRegradeWordCutoff, languageUseRegradeWordCutoff } from '../../../shared/constants.js';
import {
  GENERAL_SUBJECT,
  MAX_RULES_PER_CATEGORY,
  MEMORY_CATEGORIES,
  type MemoryField,
} from '../../../shared/memory.js';
import { clearGuestConversationId, clearGuestMessageCount } from '../../lib/guest-user-id.js';
import { useCurrentUser } from '../../lib/useCurrentUser.js';
import { Button } from '../ui/button.js';

interface ProfilePageProps {
  onBack: () => void;
}

/** A teacher preference rule as edited in the profile: free text plus its subject scope. */
interface EditableRule {
  rule: string;
  subject: string;
}

type RulesByField = Record<MemoryField, EditableRule[]>;

// The subject <select> trigger stays brand-blue (matching the display badge), but the opened option
// list should be plain white/black — options otherwise inherit the trigger's blue text, so force it.
const RULE_OPTION_STYLE = { backgroundColor: '#ffffff', color: '#000000' } as const;
const RULE_OPTION_STYLE_BOLD = { ...RULE_OPTION_STYLE, fontWeight: 700 } as const;

/** Icon shown on each rule-category tile (keyed by the category's i18n key). */
const CATEGORY_ICONS = {
  chat_and_workflow: MessageSquare,
  grading_and_feedback: ClipboardCheck,
  master_solution_design: FileText,
  grading_scheme_creation: ListChecks,
} as const;

/** The teacher's default choice of whether to always use the Fehlerindex instead of detected/merged
 * grammar rules (a per-exam override can always flip this for a specific exam). */
interface GrammarPrefs {
  alwaysUseFi: boolean;
  /** Minimum cumulative changed-word count before an answer edit auto-triggers a language-use regrade. */
  languageUseRegradeWordCutoff: number;
  /** Same idea for the content regrade an answer edit already auto-triggers; 0 = always (today's behavior). */
  contentRegradeWordCutoff: number;
}

const DEFAULT_GRAMMAR_PREFS: GrammarPrefs = {
  alwaysUseFi: false,
  languageUseRegradeWordCutoff: languageUseRegradeWordCutoff,
  contentRegradeWordCutoff: contentRegradeWordCutoff,
};

/** Empty rule lists for every memory field — the starting point before a teacher is loaded. */
const emptyRulesByField = (): RulesByField =>
  MEMORY_CATEGORIES.reduce((acc, c) => {
    acc[c.field] = [];
    return acc;
  }, {} as RulesByField);

/** Read the persisted Memory entity into editable per-field rule lists, tolerating missing data. */
const rulesFromMemory = (memory: unknown): RulesByField => {
  const result = emptyRulesByField();
  for (const { field } of MEMORY_CATEGORIES) {
    const raw = (memory as any)?.[field];
    if (!Array.isArray(raw)) continue;
    result[field] = raw
      .map(
        (item: any): EditableRule => ({
          rule: String(item?.rule ?? '').trim(),
          subject: String(item?.subject ?? GENERAL_SUBJECT) || GENERAL_SUBJECT,
        }),
      )
      .filter(r => r.rule.length > 0);
  }
  return result;
};

export function ProfilePage({ onBack }: ProfilePageProps) {
  const { t } = useTranslation();
  const { user, isLoading: isUserLoading } = useCurrentUser();

  const [teacher, setTeacher] = useState<Teacher | null>(null);
  const [isTeacherLoading, setIsTeacherLoading] = useState(false);
  const [student, setStudent] = useState<Student | null>(null);
  const [isStudentLoading, setIsStudentLoading] = useState(false);

  // A person has either a Teacher or a Student profile for their userID — whichever resolves
  // determines the role (see db/teacher.ts / db/student.ts).
  const role: 'teacher' | 'student' | undefined = teacher ? 'teacher' : student ? 'student' : undefined;

  const [isEditingPersonal, setIsEditingPersonal] = useState(false);
  const [isEditingRules, setIsEditingRules] = useState(false);
  const [isSaving, setIsSaving] = useState(false);

  const [editFirstName, setEditFirstName] = useState('');
  const [editLastName, setEditLastName] = useState('');
  const [editSchool, setEditSchool] = useState('');
  const [editSubjects, setEditSubjects] = useState<string[]>([]);
  const [subjectQuery, setSubjectQuery] = useState('');
  const [isSubjectMenuOpen, setIsSubjectMenuOpen] = useState(false);
  const [newStudentSubject, setNewStudentSubject] = useState('');
  const [isSavingStudentSubjects, setIsSavingStudentSubjects] = useState(false);

  const [rulesByField, setRulesByField] = useState<RulesByField>(emptyRulesByField);
  const [editRules, setEditRules] = useState<RulesByField>(emptyRulesByField);
  const [grammarPrefs, setGrammarPrefs] = useState<GrammarPrefs>(DEFAULT_GRAMMAR_PREFS);
  const [editGrammarPrefs, setEditGrammarPrefs] = useState<GrammarPrefs>(DEFAULT_GRAMMAR_PREFS);
  // Which rule categories are expanded. Collapsed by default so the section stays compact; the
  // per-category count stays visible in every header regardless of open/closed state.
  const [openCategories, setOpenCategories] = useState<Record<string, boolean>>({});

  const handleLogout = async () => {
    try {
      try {
        clearGuestConversationId();
      } catch (e) {}
      try {
        clearGuestMessageCount();
      } catch (e) {}
      await logout();
      onBack();
    } catch (err) {
      logger.error(err, `Logout failed:`);
    }
  };

  useEffect(() => {
    const userId = (user as any)?._id;
    if (!userId) return;

    let isMounted = true;
    setIsTeacherLoading(true);

    void getTeacherByUserIdQuery(userId)({
      firstName: 1,
      lastName: 1,
      school: 1,
      subjects: 1,
      memory: {
        chattingStyle: { rule: 1, subject: 1 },
        gradingStyle: { rule: 1, subject: 1 },
        masterSolutionStyle: { rule: 1, subject: 1 },
        gradingSchemeStyle: { rule: 1, subject: 1 },
      },
      grammarPreferences: { alwaysUseFi: 1, languageUseRegradeWordCutoff: 1, contentRegradeWordCutoff: 1 },
    })
      .then(teachers => {
        if (!isMounted) return;
        const loaded = teachers?.[0] || null;
        setTeacher(loaded);
        setRulesByField(rulesFromMemory((loaded as any)?.memory));
        const prefs = (loaded as any)?.grammarPreferences;
        setGrammarPrefs({
          alwaysUseFi: Boolean(prefs?.alwaysUseFi),
          languageUseRegradeWordCutoff:
            typeof prefs?.languageUseRegradeWordCutoff === 'number'
              ? prefs.languageUseRegradeWordCutoff
              : DEFAULT_GRAMMAR_PREFS.languageUseRegradeWordCutoff,
          contentRegradeWordCutoff:
            typeof prefs?.contentRegradeWordCutoff === 'number'
              ? prefs.contentRegradeWordCutoff
              : DEFAULT_GRAMMAR_PREFS.contentRegradeWordCutoff,
        });
      })
      .catch(logger.error)
      .finally(() => {
        if (isMounted) setIsTeacherLoading(false);
      });

    return () => {
      isMounted = false;
    };
  }, [user]);

  useEffect(() => {
    const userId = (user as any)?._id;
    if (!userId) return;

    let isMounted = true;
    setIsStudentLoading(true);

    void getStudentByUserIdQuery(userId)({
      firstName: 1,
      lastName: 1,
      school: 1,
      subjects: 1,
    })
      .then(students => {
        if (!isMounted) return;
        setStudent(students?.[0] || null);
      })
      .catch(logger.error)
      .finally(() => {
        if (isMounted) setIsStudentLoading(false);
      });

    return () => {
      isMounted = false;
    };
  }, [user]);

  const handleStartEditPersonal = () => {
    setEditFirstName(teacher?.firstName || '');
    setEditLastName(teacher?.lastName || '');
    setEditSchool(teacher?.school || '');
    setEditSubjects(teacher?.subjects?.length ? teacher.subjects : []);
    setIsEditingPersonal(true);
  };

  const handleSavePersonal = async () => {
    setIsSaving(true);
    try {
      const success = await editTeacherMutation(editFirstName, editLastName, editSchool, editSubjects)();

      if (success) {
        setTeacher(prev =>
          prev
            ? ({
                ...prev,
                firstName: editFirstName,
                lastName: editLastName,
                school: editSchool,
                subjects: editSubjects,
              } as Teacher)
            : null,
        );
        setIsEditingPersonal(false);
      } else {
        alert(t('Profile.alerts.saveError'));
      }
    } catch (error) {
      logger.error(error, `Fehler beim Speichern:`);
      alert(t('Profile.alerts.generalError'));
    } finally {
      setIsSaving(false);
    }
  };

  const addEditSubject = (value: string) => {
    const trimmed = value.trim();
    if (!trimmed) return;
    setEditSubjects(prev => (prev.some(s => s.toLowerCase() === trimmed.toLowerCase()) ? prev : [...prev, trimmed]));
    setSubjectQuery('');
  };

  const removeEditSubject = (subject: string) => {
    setEditSubjects(prev => prev.filter(s => s !== subject));
  };

  const saveStudentSubjects = async (nextSubjects: string[]) => {
    setIsSavingStudentSubjects(true);
    try {
      const success = await editStudentSubjectsMutation(nextSubjects)();
      if (success) {
        setStudent(prev => (prev ? ({ ...prev, subjects: nextSubjects } as Student) : null));
      } else {
        alert(t('Profile.alerts.saveError'));
      }
    } catch (error) {
      logger.error(error, `Fehler beim Speichern:`);
      alert(t('Profile.alerts.generalError'));
    } finally {
      setIsSavingStudentSubjects(false);
    }
  };

  const handleAddStudentSubject = () => {
    const value = newStudentSubject.trim();
    if (!value) return;
    const current = student?.subjects ?? [];
    if (current.includes(value)) {
      setNewStudentSubject('');
      return;
    }
    setNewStudentSubject('');
    void saveStudentSubjects([...current, value]);
  };

  const handleRemoveStudentSubject = (subject: string) => {
    const current = student?.subjects ?? [];
    void saveStudentSubjects(current.filter(s => s !== subject));
  };

  const handleStartEditRules = () => {
    // Deep copy so edits don't mutate the persisted snapshot until saved.
    const copy = emptyRulesByField();
    for (const { field } of MEMORY_CATEGORIES) copy[field] = rulesByField[field].map(r => ({ ...r }));
    setEditRules(copy);
    setEditGrammarPrefs({ ...grammarPrefs });
    setIsEditingRules(true);
  };

  const handleSaveRules = async () => {
    setIsSaving(true);
    try {
      // Drop blank rules a teacher may have added but not filled in before saving.
      const cleaned = emptyRulesByField();
      for (const { field } of MEMORY_CATEGORIES) {
        cleaned[field] = editRules[field]
          .map(r => ({ rule: r.rule.trim(), subject: r.subject || GENERAL_SUBJECT }))
          .filter(r => r.rule.length > 0)
          .slice(0, MAX_RULES_PER_CATEGORY);
      }

      const success = await editTeacherMemoryMutation(JSON.stringify(cleaned))();
      const prefsSuccess = await updateGrammarPreferencesMutation(
        editGrammarPrefs.alwaysUseFi,
        Math.max(0, Math.round(editGrammarPrefs.languageUseRegradeWordCutoff)),
        Math.max(0, Math.round(editGrammarPrefs.contentRegradeWordCutoff)),
      )();
      if (success && prefsSuccess) {
        setRulesByField(cleaned);
        setGrammarPrefs({ ...editGrammarPrefs });
        setIsEditingRules(false);
      } else {
        alert(t('Profile.alerts.saveError'));
      }
    } catch (error) {
      logger.error(error, `Fehler beim Speichern der Regeln:`);
      alert(t('Profile.alerts.generalError'));
    } finally {
      setIsSaving(false);
    }
  };

  const updateRule = (field: MemoryField, index: number, patch: Partial<EditableRule>) => {
    setEditRules(prev => ({
      ...prev,
      [field]: prev[field].map((r, i) => (i === index ? { ...r, ...patch } : r)),
    }));
  };

  const removeRule = (field: MemoryField, index: number) => {
    setEditRules(prev => ({ ...prev, [field]: prev[field].filter((_, i) => i !== index) }));
  };

  const addRule = (field: MemoryField) => {
    setEditRules(prev =>
      prev[field].length >= MAX_RULES_PER_CATEGORY
        ? prev
        : { ...prev, [field]: [...prev[field], { rule: '', subject: GENERAL_SUBJECT }] },
    );
  };

  // Subject scope picker offers ALL subjects; the teacher's own subjects are surfaced in a separate
  // group at the top, the remaining subjects below.
  const ownSubjects = useMemo(() => teacher?.subjects ?? [], [teacher]);
  const otherSubjects = useMemo(() => ALL_SUBJECTS.filter(s => !ownSubjects.includes(s)), [ownSubjects]);

  const subjectLabel = (subject: string) =>
    subject === GENERAL_SUBJECT
      ? t('Profile.rules.general')
      : isSubject(subject)
        ? t(`Subjects.${subject}`)
        : subject;

  const toggleCategory = (key: string) => setOpenCategories(prev => ({ ...prev, [key]: !prev[key] }));

  // Students only chat with the assistant and get graded — master solution design and grading
  // scheme creation are teacher-only workflows, so those categories stay hidden for students.
  const visibleMemoryCategories =
    role === 'student'
      ? MEMORY_CATEGORIES.filter(cat => cat.key === 'chat_and_workflow' || cat.key === 'grading_and_feedback')
      : MEMORY_CATEGORIES;

  const isLoading = isUserLoading || isTeacherLoading || isStudentLoading;
  const displayName = teacher
    ? `${teacher.firstName} ${teacher.lastName}`
    : student
      ? `${student.firstName} ${student.lastName}`
      : user?.username || t('Profile.personal.defaultUser');
  const displayEmail = user?.email || '';

  const profileSubjects = role === 'student' ? student?.subjects : teacher?.subjects;
  const subjects = profileSubjects?.length ? profileSubjects : (['math', 'computer_science'] as Subject[]);

  // Canonical subjects not yet selected, filtered by the combobox query (matched against the
  // localized label so typing "Mathe" still finds "math").
  const trimmedSubjectQuery = subjectQuery.trim();
  const subjectCandidates = ALL_SUBJECTS.filter(subj => !editSubjects.includes(subj)).filter(subj =>
    trimmedSubjectQuery ? t(`Subjects.${subj}`).toLowerCase().includes(trimmedSubjectQuery.toLowerCase()) : true,
  );
  const canAddCustomSubject =
    trimmedSubjectQuery.length > 0 &&
    !subjectCandidates.some(subj => t(`Subjects.${subj}`).toLowerCase() === trimmedSubjectQuery.toLowerCase()) &&
    !editSubjects.some(s => s.toLowerCase() === trimmedSubjectQuery.toLowerCase());

  const SectionActions = ({
    isEditing,
    onStart,
    onCancel,
    onSave,
  }: {
    isEditing: boolean;
    onStart: () => void;
    onCancel: () => void;
    onSave: () => void;
  }) => (
    <div className="absolute top-4 right-4 flex gap-2">
      {isEditing ? (
        <>
          <Button
            onClick={onCancel}
            variant="ghost"
            size="sm"
            className="text-muted-foreground hover:bg-accent hover:text-accent-foreground"
            disabled={isSaving}
          >
            <X className="h-4 w-4 sm:mr-1" /> <span className="hidden sm:inline">{t('Profile.actions.cancel')}</span>
          </Button>
          <Button
            onClick={onSave}
            size="sm"
            className="bg-brand-strong hover:bg-brand-700 text-white"
            disabled={isSaving}
          >
            {isSaving ? (
              '...'
            ) : (
              <>
                <Check className="h-4 w-4 sm:mr-1" />{' '}
                <span className="hidden sm:inline">{t('Profile.actions.save')}</span>
              </>
            )}
          </Button>
        </>
      ) : (
        <Button onClick={onStart} variant="ghost" size="sm" className="text-brand-text hover:bg-accent gap-2">
          <Pencil className="h-4 w-4" />
          <span className="hidden sm:inline font-semibold">{t('Profile.actions.edit')}</span>
        </Button>
      )}
    </div>
  );

  return (
    <div className="flex flex-col h-full w-full bg-background overflow-y-auto">
      <div className="sticky top-0 z-10 border-b border-border bg-card px-4 sm:px-8 py-2 sm:py-4 shadow-sm">
        <div className="flex items-center justify-start gap-2 max-w-4xl mx-auto">
          <Button
            onClick={onBack}
            variant="ghost"
            size="icon"
            className="text-muted-foreground hover:text-foreground hover:bg-accent"
          >
            <ArrowLeft className="h-5 w-5" />
          </Button>
          <h1 className="text-xl sm:text-xl font-bold text-foreground tracking-tight">{t('Profile.title')}</h1>
        </div>
      </div>

      <div className="flex-1 px-4 sm:px-8 py-8">
        <div className="max-w-4xl mx-auto space-y-8 animate-in fade-in duration-500">
          <section className="bg-card rounded-xl p-6 sm:p-8 border border-border shadow-sm relative">
            {role !== 'student' && !isEditingPersonal && (
              <div className="absolute top-4 right-4">
                <Button
                  onClick={handleStartEditPersonal}
                  variant="ghost"
                  size="sm"
                  className="text-brand-text hover:bg-accent gap-2"
                >
                  <Pencil className="h-4 w-4" />
                  <span className="hidden sm:inline font-semibold">{t('Profile.actions.edit')}</span>
                </Button>
              </div>
            )}

            <div className="flex flex-col sm:flex-row items-start gap-6">
              <div className="w-24 h-24 bg-brand-strong rounded-2xl flex items-center justify-center flex-shrink-0 shadow-sm">
                <User className="h-12 w-12 text-white" />
              </div>
              <div className="flex-1 space-y-4 pt-1 w-full">
                {isEditingPersonal ? (
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                      <label className="text-sm font-medium text-muted-foreground mb-1 block">
                        {t('Profile.personal.firstName')}
                      </label>
                      <input
                        type="text"
                        value={editFirstName}
                        onChange={e => setEditFirstName(e.target.value)}
                        className="w-full px-3 py-2 bg-background text-foreground border border-input rounded-lg focus:outline-none focus:ring-2 focus:ring-brand-primary"
                      />
                    </div>
                    <div>
                      <label className="text-sm font-medium text-muted-foreground mb-1 block">
                        {t('Profile.personal.lastName')}
                      </label>
                      <input
                        type="text"
                        value={editLastName}
                        onChange={e => setEditLastName(e.target.value)}
                        className="w-full px-3 py-2 bg-background text-foreground border border-input rounded-lg focus:outline-none focus:ring-2 focus:ring-brand-primary"
                      />
                    </div>
                  </div>
                ) : (
                  <div>
                    <p className="text-sm font-medium text-muted-foreground mb-1">{t('Profile.personal.name')}</p>
                    <div className="flex items-center gap-2 flex-wrap">
                      <p className="text-xl font-medium text-foreground">{isLoading ? '...' : displayName}</p>
                      {role && (
                        <Badge>
                          {role === 'teacher' ? t('Profile.personal.role.teacher') : t('Profile.personal.role.student')}
                        </Badge>
                      )}
                    </div>
                  </div>
                )}

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 pt-2">
                  <div>
                    <p className="text-sm font-medium text-muted-foreground mb-1 flex items-center gap-2">
                      <School className="h-4 w-4" /> {t('Profile.personal.school')}
                    </p>
                    {isEditingPersonal ? (
                      <input
                        type="text"
                        value={editSchool}
                        onChange={e => setEditSchool(e.target.value)}
                        className="w-full px-3 py-2 bg-background text-foreground border border-input rounded-lg focus:outline-none focus:ring-2 focus:ring-brand-primary"
                      />
                    ) : (
                      <p className="text-lg text-foreground">
                        {(role === 'student' ? student?.school : teacher?.school) || t('Profile.personal.noSchool')}
                      </p>
                    )}
                  </div>
                  <div>
                    <p className="text-sm font-medium text-muted-foreground mb-1 flex items-center gap-2">
                      <Mail className="h-4 w-4" /> {t('Profile.personal.email')}
                    </p>
                    <p className="text-lg text-foreground">{isLoading ? '...' : displayEmail}</p>
                    {isEditingPersonal && (
                      <p className="text-xs text-muted-foreground/70 mt-1">{t('Profile.personal.emailHint')}</p>
                    )}
                  </div>
                </div>

                <div className="pt-2">
                  <p className="text-sm font-medium text-muted-foreground mb-2 flex items-center gap-2">
                    <BookOpen className="h-4 w-4" /> {t('Profile.teaching.subjects')}
                  </p>

                  {role === 'student' ? (
                    <div className="space-y-3">
                      <div className="flex flex-wrap gap-2">
                        {subjects.map((s, i) => (
                          <span
                            key={i}
                            className="flex items-center gap-1.5 px-3 py-1.5 bg-brand-surface text-brand-text border border-brand-border rounded-lg text-sm font-medium"
                          >
                            {s}
                            <button
                              type="button"
                              onClick={() => handleRemoveStudentSubject(s)}
                              disabled={isSavingStudentSubjects}
                              aria-label={t('Profile.actions.remove')}
                              className="hover:text-destructive disabled:opacity-50"
                            >
                              <X className="h-3.5 w-3.5" />
                            </button>
                          </span>
                        ))}
                      </div>
                      <div className="flex gap-2 max-w-sm">
                        <input
                          type="text"
                          value={newStudentSubject}
                          onChange={e => setNewStudentSubject(e.target.value)}
                          onKeyDown={e => {
                            if (e.key === 'Enter') {
                              e.preventDefault();
                              handleAddStudentSubject();
                            }
                          }}
                          placeholder={t('Profile.teaching.addSubjectPlaceholder')}
                          disabled={isSavingStudentSubjects}
                          className="flex-1 px-3 py-2 bg-background text-foreground border border-input rounded-lg focus:outline-none focus:ring-2 focus:ring-brand-primary"
                        />
                        <Button
                          type="button"
                          onClick={handleAddStudentSubject}
                          disabled={isSavingStudentSubjects || !newStudentSubject.trim()}
                          size="sm"
                          className="bg-brand-strong hover:bg-brand-700 text-white"
                        >
                          <Plus className="h-4 w-4" />
                        </Button>
                      </div>
                    </div>
                  ) : isEditingPersonal ? (
                    <div className="relative">
                      <div className="w-full min-h-10 flex flex-wrap items-center gap-1.5 px-2 py-1.5 bg-background border border-input rounded-lg focus-within:outline-none focus-within:ring-2 focus-within:ring-brand-primary">
                        {editSubjects.map(s => (
                          <span
                            key={s}
                            className="flex items-center gap-1 pl-2.5 pr-1.5 py-1 bg-brand-strong text-white rounded-md text-sm font-medium"
                          >
                            {isSubject(s) ? t(`Subjects.${s}`) : s}
                            <button
                              type="button"
                              onClick={() => removeEditSubject(s)}
                              aria-label={t('Profile.actions.remove')}
                              className="hover:opacity-70"
                            >
                              <X className="h-3.5 w-3.5" />
                            </button>
                          </span>
                        ))}
                        <input
                          type="text"
                          value={subjectQuery}
                          onChange={e => {
                            setSubjectQuery(e.target.value);
                            setIsSubjectMenuOpen(true);
                          }}
                          onFocus={() => setIsSubjectMenuOpen(true)}
                          onBlur={() => setTimeout(() => setIsSubjectMenuOpen(false), 120)}
                          onKeyDown={e => {
                            if (e.key === 'Enter') {
                              e.preventDefault();
                              const firstCandidate = subjectCandidates[0];
                              if (firstCandidate) {
                                addEditSubject(firstCandidate);
                              } else if (canAddCustomSubject) {
                                addEditSubject(trimmedSubjectQuery);
                              }
                            } else if (e.key === 'Escape') {
                              setIsSubjectMenuOpen(false);
                            }
                          }}
                          placeholder={editSubjects.length === 0 ? t('Profile.teaching.addSubjectPlaceholder') : ''}
                          className="flex-1 min-w-[140px] bg-transparent text-foreground text-sm px-1 py-1 border-0 outline-none focus:outline-none focus:ring-0"
                        />
                      </div>
                      {isSubjectMenuOpen && (subjectCandidates.length > 0 || canAddCustomSubject) && (
                        <div className="absolute left-0 right-0 top-full z-20 mt-1 max-h-56 overflow-y-auto bg-popover text-popover-foreground border border-border rounded-lg shadow-lg py-1">
                          {subjectCandidates.map(subj => (
                            <button
                              key={subj}
                              type="button"
                              onMouseDown={e => e.preventDefault()}
                              onClick={() => addEditSubject(subj)}
                              className="w-full text-left px-3 py-2 text-sm text-foreground hover:bg-accent transition-colors"
                            >
                              {t(`Subjects.${subj}`)}
                            </button>
                          ))}
                          {canAddCustomSubject && (
                            <button
                              type="button"
                              onMouseDown={e => e.preventDefault()}
                              onClick={() => addEditSubject(trimmedSubjectQuery)}
                              className="w-full text-left px-3 py-2 text-sm text-brand-text font-medium hover:bg-accent transition-colors border-t border-border"
                            >
                              {t('Profile.teaching.addCustomSubject', { value: trimmedSubjectQuery })}
                            </button>
                          )}
                        </div>
                      )}
                    </div>
                  ) : (
                    <div className="flex flex-wrap gap-2">
                      {subjects.map((s, i) => (
                        <span
                          key={i}
                          className="px-3 py-1.5 bg-brand-surface text-brand-text border border-brand-border rounded-lg text-sm font-medium"
                        >
                          {isSubject(s) ? t(`Subjects.${s}`) : s}
                        </span>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            </div>

            {role !== 'student' && isEditingPersonal && (
              <div className="flex justify-end gap-2 mt-6 pt-4 border-t border-border">
                <Button
                  onClick={() => setIsEditingPersonal(false)}
                  variant="ghost"
                  size="sm"
                  className="text-muted-foreground hover:bg-accent hover:text-accent-foreground"
                  disabled={isSaving}
                >
                  <X className="h-4 w-4 sm:mr-1" /> <span className="hidden sm:inline">{t('Profile.actions.cancel')}</span>
                </Button>
                <Button
                  onClick={handleSavePersonal}
                  size="sm"
                  className="bg-brand-strong hover:bg-brand-700 text-white"
                  disabled={isSaving}
                >
                  {isSaving ? (
                    '...'
                  ) : (
                    <>
                      <Check className="h-4 w-4 sm:mr-1" />{' '}
                      <span className="hidden sm:inline">{t('Profile.actions.save')}</span>
                    </>
                  )}
                </Button>
              </div>
            )}
          </section>

          <section className="bg-card rounded-xl p-6 sm:p-8 border border-border shadow-sm relative">
            <SectionActions
              isEditing={isEditingRules}
              onStart={handleStartEditRules}
              onCancel={() => setIsEditingRules(false)}
              onSave={handleSaveRules}
            />

            <h2 className="text-lg font-semibold text-foreground mb-2 flex items-center gap-2">
              <Brain className="h-5 w-5 text-brand-text" /> {t('Profile.rules.title')}
            </h2>
            <p className="text-sm text-muted-foreground mb-6 max-w-2xl pr-16 sm:pr-24">
              {t('Profile.rules.description')}
            </p>

            <div className="space-y-3">
              {visibleMemoryCategories.map(cat => {
                const list = isEditingRules ? editRules[cat.field] : rulesByField[cat.field];
                const atLimit = list.length >= MAX_RULES_PER_CATEGORY;
                const isOpen = !!openCategories[cat.key];
                const Icon = CATEGORY_ICONS[cat.key] ?? Brain;
                return (
                  <div key={cat.key} className="border border-border bg-background rounded-lg overflow-hidden">
                    <button
                      type="button"
                      onClick={() => toggleCategory(cat.key)}
                      aria-expanded={isOpen}
                      className="w-full p-4 flex items-center justify-between gap-3 text-left hover:bg-accent transition-colors cursor-pointer"
                    >
                      <div className="flex items-center gap-4 min-w-0">
                        <Icon className="h-5 w-5 text-muted-foreground shrink-0" />
                        <div className="min-w-0">
                          <p className="font-medium text-foreground">{t(`Profile.rules.categories.${cat.i18nKey}`)}</p>
                          <p className="text-sm text-muted-foreground mt-1">
                            {t(`Profile.rules.categoryDescs.${cat.i18nKey}`)}
                          </p>
                        </div>
                      </div>
                      <div className="flex items-center gap-3 shrink-0">
                        <span
                          className={`text-xs font-semibold px-2 py-1 rounded ${
                            atLimit ? 'bg-amber-50 text-amber-700' : 'bg-muted text-muted-foreground'
                          }`}
                          title={t('Profile.rules.limitHint', { max: MAX_RULES_PER_CATEGORY })}
                        >
                          {list.length}/{MAX_RULES_PER_CATEGORY}
                        </span>
                        <ChevronDown
                          className={`h-5 w-5 text-muted-foreground transition-transform ${isOpen ? 'rotate-180' : ''}`}
                        />
                      </div>
                    </button>

                    {isOpen &&
                      (isEditingRules ? (
                        <div className="px-4 pb-4 pt-3 border-t border-border space-y-2">
                          {list.map((r, i) => {
                            return (
                              <div
                                key={i}
                                className="flex items-start gap-2.5 p-2.5 bg-card border border-border rounded-lg focus-within:border-brand-primary focus-within:ring-2 focus-within:ring-brand-primary/40 transition-colors"
                              >
                                <select
                                  value={r.subject}
                                  onChange={e => updateRule(cat.field, i, { subject: e.target.value })}
                                  className="shrink-0 mt-0.5 px-2 py-0.5 bg-brand-surface text-brand-text border border-brand-border rounded text-xs font-medium cursor-pointer focus:outline-none"
                                >
                                  <option value={GENERAL_SUBJECT} style={RULE_OPTION_STYLE_BOLD}>
                                    {subjectLabel(GENERAL_SUBJECT)}
                                  </option>
                                  {ownSubjects.map(s => (
                                    <option key={s} value={s} style={RULE_OPTION_STYLE_BOLD}>
                                      {subjectLabel(s)}
                                    </option>
                                  ))}
                                  {otherSubjects.length > 0 && (
                                    <option disabled style={{ ...RULE_OPTION_STYLE, color: '#9ca3af' }}>
                                      ──────────
                                    </option>
                                  )}
                                  {otherSubjects.map(s => (
                                    <option key={s} value={s} style={RULE_OPTION_STYLE}>
                                      {subjectLabel(s)}
                                    </option>
                                  ))}
                                </select>
                                <textarea
                                  ref={el => {
                                    // Auto-grow so the rule wraps onto multiple lines instead of
                                    // scrolling horizontally, matching the display row.
                                    if (el) {
                                      el.style.height = 'auto';
                                      el.style.height = `${el.scrollHeight}px`;
                                    }
                                  }}
                                  rows={1}
                                  value={r.rule}
                                  onChange={e => updateRule(cat.field, i, { rule: e.target.value })}
                                  placeholder={t('Profile.rules.placeholder')}
                                  className="flex-1 min-w-0 p-0 border-0 bg-transparent text-sm text-foreground placeholder:text-muted-foreground/60 focus:outline-none focus:ring-0 resize-none overflow-hidden break-words"
                                />
                                <button
                                  onClick={() => removeRule(cat.field, i)}
                                  className="shrink-0 -my-0.5 p-1 text-muted-foreground hover:text-destructive rounded transition-colors"
                                  aria-label={t('Profile.rules.removeRule')}
                                >
                                  <X className="h-4 w-4" />
                                </button>
                              </div>
                            );
                          })}
                          <button
                            onClick={() => addRule(cat.field)}
                            disabled={atLimit}
                            className="w-full flex items-center justify-center gap-1.5 p-2.5 rounded-lg border border-dashed border-border text-sm font-medium text-brand-text hover:bg-accent transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
                          >
                            <Plus className="h-4 w-4" />
                            {atLimit ? t('Profile.rules.limitReached') : t('Profile.rules.addRule')}
                          </button>
                        </div>
                      ) : list.length ? (
                        <div className="px-4 pb-4 pt-3 border-t border-border space-y-2">
                          {list.map((r, i) => (
                            <div
                              key={i}
                              className="flex items-start gap-2.5 p-2.5 bg-card border border-border rounded-lg"
                            >
                              <span className="shrink-0 mt-0.5 px-2 py-0.5 bg-brand-surface text-brand-text border border-brand-border rounded text-xs font-medium">
                                {subjectLabel(r.subject)}
                              </span>
                              <span className="text-sm text-foreground break-words">{r.rule}</span>
                            </div>
                          ))}
                        </div>
                      ) : (
                        <p className="px-4 pb-4 pt-3 border-t border-border text-sm italic text-muted-foreground/70">
                          {t('Profile.rules.empty')}
                        </p>
                      ))}
                  </div>
                );
              })}
            </div>

            {/* Standalone grammar-grading default: always use Fehlerindex instead of detected/merged
                grammar rules. Unrelated to the rule categories above — a per-exam override can always
                flip this for a specific exam. Grading is a teacher-only workflow, so hidden for students. */}
            {role !== 'student' && (
              <div className="mt-3 border border-border bg-background rounded-lg p-4">
                <label className="flex items-start gap-2.5 cursor-pointer">
                  <SpellCheck className="h-5 w-5 text-muted-foreground shrink-0" />
                  <span className="flex-1">
                    <span className="block text-sm font-medium text-foreground">
                      {t('Profile.rules.grammar.alwaysUseFi')}
                    </span>
                    <span className="block text-xs text-muted-foreground mt-0.5">
                      {t('Profile.rules.grammar.alwaysUseFiDesc')}
                    </span>
                  </span>
                  <input
                    type="checkbox"
                    checked={isEditingRules ? editGrammarPrefs.alwaysUseFi : grammarPrefs.alwaysUseFi}
                    disabled={!isEditingRules}
                    onChange={e => setEditGrammarPrefs(prev => ({ ...prev, alwaysUseFi: e.target.checked }))}
                    className="mt-0.5 h-4 w-4 accent-brand-strong shrink-0"
                  />
                </label>
              </div>
            )}

            {/* Word-cutoff auto-regrade thresholds: how many changed words in an edited answer before
                that edit auto-triggers a regrade. Content defaults to 0 (always, today's behavior);
                language-use defaults to 5 (skip a regrade for a single fixed typo). */}
            {role !== 'student' && (
              <div className="mt-3 border border-border bg-background rounded-lg p-4 space-y-3">
                <div className="flex items-start gap-2.5">
                  <span className="flex-1">
                    <span className="block text-sm font-medium text-foreground">
                      {t('Profile.rules.grammar.languageUseRegradeWordCutoff')}
                    </span>
                    <span className="block text-xs text-muted-foreground mt-0.5">
                      {t('Profile.rules.grammar.languageUseRegradeWordCutoffDesc')}
                    </span>
                  </span>
                  <input
                    type="number"
                    min={0}
                    value={
                      isEditingRules
                        ? editGrammarPrefs.languageUseRegradeWordCutoff
                        : grammarPrefs.languageUseRegradeWordCutoff
                    }
                    disabled={!isEditingRules}
                    onChange={e =>
                      setEditGrammarPrefs(prev => ({
                        ...prev,
                        languageUseRegradeWordCutoff: Math.max(0, Number(e.target.value) || 0),
                      }))
                    }
                    className="w-20 shrink-0 px-2 py-1 bg-background text-foreground border border-input rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-brand-primary disabled:opacity-60"
                  />
                </div>
                <div className="flex items-start gap-2.5">
                  <span className="flex-1">
                    <span className="block text-sm font-medium text-foreground">
                      {t('Profile.rules.grammar.contentRegradeWordCutoff')}
                    </span>
                    <span className="block text-xs text-muted-foreground mt-0.5">
                      {t('Profile.rules.grammar.contentRegradeWordCutoffDesc')}
                    </span>
                  </span>
                  <input
                    type="number"
                    min={0}
                    value={
                      isEditingRules ? editGrammarPrefs.contentRegradeWordCutoff : grammarPrefs.contentRegradeWordCutoff
                    }
                    disabled={!isEditingRules}
                    onChange={e =>
                      setEditGrammarPrefs(prev => ({
                        ...prev,
                        contentRegradeWordCutoff: Math.max(0, Number(e.target.value) || 0),
                      }))
                    }
                    className="w-20 shrink-0 px-2 py-1 bg-background text-foreground border border-input rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-brand-primary disabled:opacity-60"
                  />
                </div>
              </div>
            )}
          </section>

          <section className="bg-card rounded-xl p-6 sm:p-8 border border-border shadow-sm overflow-hidden">
            <h2 className="text-lg font-semibold text-foreground mb-6 flex items-center gap-2">
              <Shield className="h-5 w-5 text-muted-foreground" /> {t('Profile.security.title')}
            </h2>

            <div className="space-y-3">
              <div className="p-4 border border-border bg-background rounded-lg hover:bg-accent transition-colors cursor-pointer flex items-center justify-between group">
                <div className="flex items-center gap-4">
                  <Key className="h-5 w-5 text-muted-foreground" />
                  <div>
                    <p className="font-medium text-foreground">{t('Profile.security.changePassword')}</p>
                    <p className="text-sm text-muted-foreground mt-1">{t('Profile.security.changePasswordDesc')}</p>
                  </div>
                </div>
                <span className="text-xs font-semibold bg-muted text-muted-foreground px-2 py-1 rounded">
                  {t('Profile.badges.soon')}
                </span>
              </div>

              <div
                onClick={handleLogout}
                className="p-4 border border-destructive/20 bg-destructive/5 rounded-lg hover:bg-destructive/10 transition-colors cursor-pointer flex items-center gap-4 text-destructive"
              >
                <LogOut className="h-5 w-5" />
                <div>
                  <p className="font-medium">{t('Profile.security.logout')}</p>
                  <p className="text-sm text-destructive/80 mt-1">{t('Profile.security.logoutDesc')}</p>
                </div>
              </div>
            </div>
          </section>
        </div>
      </div>
    </div>
  );
}
